import React, { memo, useMemo, useState, useCallback, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';

// styles
import { Wrapper, Message, Error, Placeholder, Shimmer, CategorySwitchOverlay } from './styles';

// components
import Suggestion from './components/Suggestion';
import StoreBanner from 'shared/components/StoreBanner';
import SwitchTransitionScreen from 'shared/components/StoreBanner/components/SwitchTransitionScreen';

// constants
import { SOMETHING_WENT_WRONG, NO_RESULTS_FOUND } from './text';
import { ISuggestion, ICON_TYPE } from 'shared/store/globalSearch/types';

// helpers
import { getRecentSearches } from 'shared/components/GlobalSearch/helpers';
import { ROLES } from 'shared/constants';

// hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

interface IAutoCompleteResultsProps {
  isVisible: boolean;
  searchTerm: string;
  suggestions: ISuggestion[];
  isError: boolean;
  isFetching: boolean;
  trendingSearches: string[];
  isRipple: boolean;
  maxRecentSearches: number;
  mwebSuggestions: string[];
}

function AutoCompleteResults({
  isVisible,
  searchTerm,
  suggestions,
  isError,
  isFetching,
  trendingSearches,
  mwebSuggestions,
  isRipple,
  maxRecentSearches,
}: IAutoCompleteResultsProps) {
  const searches = getRecentSearches();
  const recentSearches = useMemo(
    () => searches.map((e) => `${e.suggestionWord} ${e.suggestionSubtitle || ''}`.trim()),
    [searches]
  );

  // @ts-ignore: any since store can't be hardcoded as enums
  const storeSwitchBanner = useRemoteConfig('storeSwitchBanner');

  const [activeCategorySwitch, setActiveCategorySwitch] = useState<{
    storeToRedirect: string;
    redirectUrl: string;
    productName: string;
  } | null>(null);

  useEffect(() => {
    return () => {
      setActiveCategorySwitch(null);
    };
  }, []);

  const handleCategorySwitch = useCallback(
    (storeToRedirect: string, redirectUrl: string, productName: string) => {
      setActiveCategorySwitch({ storeToRedirect, redirectUrl, productName });
    },
    []
  );

  const dismissCategorySwitch = useCallback(() => setActiveCategorySwitch(null), []);

  const trendingSuggestions = isRipple ? mwebSuggestions : trendingSearches;

  const trendingSearchesInLowercase = trendingSuggestions.map((item) =>
    item.trim().toLocaleLowerCase()
  );

  const recentSearchesInLowercase = recentSearches.map((item) => item.trim().toLowerCase());

  if (!isVisible) {
    return null;
  }

  // Keep switch overlay mounted even if suggestions refetch / empty while active
  if (activeCategorySwitch) {
    const { storeToRedirect, redirectUrl, productName } = activeCategorySwitch;
    // @ts-ignore: any since store can't be hardcoded as enums
    const categorySwitchConfig = storeSwitchBanner?.[storeToRedirect]?.categoryStoreSwitch;
    // @ts-ignore: any since store can't be hardcoded as enums
    const transitionScreenConfig = storeSwitchBanner?.[storeToRedirect]?.transitionScreen;

    if (isRipple && categorySwitchConfig?.switchWithBannerMweb === false) {
      return (
        <SwitchTransitionScreen
          storeToRedirect={storeToRedirect}
          customText={transitionScreenConfig?.text?.replace('{term}', productName)}
          onRedirect={() => {
            window.location.href = redirectUrl;
          }}
        />
      );
    }
    return (
      <CategorySwitchOverlay>
        <StoreBanner
          storeToRedirect={storeToRedirect}
          currentStoreProductCount={0}
          isSearch={false}
          productName={productName}
          redirectUrl={redirectUrl}
          onDismiss={dismissCategorySwitch}
        />
      </CategorySwitchOverlay>
    );
  }

  if (isFetching) {
    return (
      <Placeholder data-test="placeholderWithShimmer">
        <Shimmer />
        <Shimmer />
        <Shimmer />
        <Shimmer />
        <Shimmer />
      </Placeholder>
    );
  }

  if (isError) {
    return <Error data-test="autoSuggestResults-Error">{SOMETHING_WENT_WRONG}</Error>;
  }

  if (isEmpty(suggestions)) {
    return (
      <Message
        data-test="autoSuggestResults-Message"
        data-cs-encrypt
      >{`${NO_RESULTS_FOUND}"${searchTerm}"`}</Message>
    );
  }

  return (
    <Wrapper
      data-test="autoSuggestResults-container"
      role={ROLES.LISTBOX}
      aria-label="Search suggestions"
      aria-live="polite"
      aria-atomic="false"
    >
      {suggestions.map((suggestion: ISuggestion, idx: number) => {
        const { suggestionWord, suggestionSubtitle } = suggestion;
        const term = `${suggestionWord} ${suggestionSubtitle ? suggestionSubtitle : ''}`
          .trim()
          .toLocaleLowerCase();

        let icon = ICON_TYPE.SEARCH;

        if (recentSearchesInLowercase.includes(term)) {
          icon = ICON_TYPE.RECENT;
        } else if (trendingSearchesInLowercase.includes(term)) {
          icon = ICON_TYPE.TRENDING;
        }

        return (
          <Suggestion
            key={idx}
            term={searchTerm}
            icon={icon}
            isRipple={isRipple}
            indexValue={idx}
            maxRecentSearches={maxRecentSearches}
            suggestion={suggestion}
            onCategorySwitch={handleCategorySwitch}
          />
        );
      })}
    </Wrapper>
  );
}

export default memo(AutoCompleteResults);
